<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import * as XLSX from 'xlsx'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import { mapVhqRowsToSheet, formatVhqDate, type VhqExportRow } from '@/utils/vhqExport'
import type ResponseData from '@/types/ResponseData'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableSearch, TablePagination, TableEmptyState, TableFilterDropdown, TableDateRange } from '@/components/common/table'
import { Button } from '@/components/ui/button'
import { Eye, FileSpreadsheet, LoaderCircle } from 'lucide-vue-next'

export default defineComponent({
  name: 'vhq-list',
  components: {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableSearch,
    TablePagination,
    TableEmptyState,
    TableFilterDropdown,
    TableDateRange,
    Button,
    Eye,
    FileSpreadsheet,
    LoaderCircle,
    PageBreadcrumb,
  },
  data() {
    return {
      rows: [] as VhqExportRow[],
      filterText: '',
      filterPackage: '',
      filterStatus: '',
      filterCompanyType: '',
      filterDateFrom: '',
      filterDateTo: '',
      loaded: false,
      exporting: false,
      currentPage: 1,
      lastPage: 1,
      total: 0,
      searchDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      router: useRouter(),
    }
  },
  watch: {
    filterPackage() { this.resetAndLoad() },
    filterStatus() { this.resetAndLoad() },
    filterCompanyType() { this.resetAndLoad() },
    filterText() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => this.resetAndLoad(), 400)
    },
  },
  methods: {
    async loadData() {
      this.loaded = false
      try {
        const res: ResponseData = await HeadquartersDataService.getVhqOverview(
          this.currentPage,
          this.filterText,
          this.filterPackage,
          this.filterStatus,
          this.filterCompanyType,
          this.filterDateFrom,
          this.filterDateTo
        )
        const pagination = res.data.data
        this.rows = pagination.data
        this.currentPage = pagination.current_page
        this.lastPage = pagination.last_page
        this.total = pagination.total
      } catch (e: any) {
        toast.error('Chyba: ' + e.message)
      } finally {
        this.loaded = true
      }
    },
    resetAndLoad() {
      this.currentPage = 1
      this.loadData()
    },
    dateRangeChanged(range: { start: string; end: string } | null) {
      this.filterDateFrom = range?.start ?? ''
      this.filterDateTo = range?.end ?? ''
      this.resetAndLoad()
    },
    changePage(page: number) {
      if (page < 1 || page > this.lastPage) return
      this.currentPage = page
      this.loadData()
    },
    async exportToExcel() {
      this.exporting = true
      try {
        const res: ResponseData = await HeadquartersDataService.getVhqExport(
          this.filterText,
          this.filterPackage,
          this.filterStatus,
          this.filterCompanyType,
          this.filterDateFrom,
          this.filterDateTo
        )
        const rows: VhqExportRow[] = res.data.data
        if (!rows.length) {
          toast.warning('Žiadne záznamy na export')
          return
        }
        const worksheet = XLSX.utils.aoa_to_sheet(mapVhqRowsToSheet(rows))
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'VHQ')
        const date = new Date().toISOString().slice(0, 10)
        XLSX.writeFile(workbook, `vhq-export-${date}.xlsx`)
      } catch (e: any) {
        toast.error('Chyba: ' + e.message)
      } finally {
        this.exporting = false
      }
    },
    getDateStatus(date: string | null): 'expired' | 'warning' | 'ok' | 'none' {
      if (!date) return 'none'
      const expiry = new Date(date)
      const now = new Date()
      const warningDate = new Date()
      warningDate.setDate(warningDate.getDate() + 15)
      if (expiry < now) return 'expired'
      if (expiry <= warningDate) return 'warning'
      return 'ok'
    },
    goToDetail(id: number) {
      this.router.push({ name: 'VHQ Detail', params: { id } })
    },
    formatVhqDate(date: string | null): string {
      return formatVhqDate(date)
    },
  },
  mounted() {
    this.loadData()
  },
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <PageBreadcrumb page-title="Virtuálne sídla" />
      </div>
      <TableCard>
        <template #controls>
          <div class="flex flex-wrap items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex-1 min-w-0 sm:w-96">
              <TableSearch v-model="filterText" placeholder="Hľadať podľa názvu alebo IČO..." />
            </div>
            <TableFilterDropdown
              :options="[
                { value: '', label: 'Všetky balíky' },
                { value: 'Mini', label: 'Mini' },
                { value: 'Ideal', label: 'Ideal' },
                { value: 'Premium', label: 'Premium' },
                { value: 'Extra', label: 'Extra' },
                { value: 'Individual', label: 'Individual' },
                { value: 'Základný trvalý pobyt v Bratislave', label: 'Trvalý pobyt - Základný' },
                { value: 'Najlepší trvalý pobyt v Bratislave', label: 'Trvalý pobyt - Najlepší' },
              ]"
              label="Balík"
              :model-value="filterPackage"
              @update:model-value="filterPackage = $event"
            />
            <TableFilterDropdown
              :options="[
                { value: '', label: 'Všetky stavy' },
                { value: 'active', label: 'Aktívne' },
                { value: 'expired', label: 'Expirované' },
              ]"
              label="Stav"
              :model-value="filterStatus"
              @update:model-value="filterStatus = $event"
            />
            <TableFilterDropdown
              :options="[
                { value: '', label: 'Všetky typy' },
                { value: '1', label: 'Fyzická osoba' },
                { value: '2', label: 'Právnická osoba' },
                { value: '3', label: 'Trvalý pobyt' },
              ]"
              label="Typ"
              :model-value="filterCompanyType"
              @update:model-value="filterCompanyType = $event"
            />
            <TableDateRange
              :model-value="{ start: filterDateFrom, end: filterDateTo }"
              start-placeholder="Zaplatené od"
              end-placeholder="do"
              @update:model-value="dateRangeChanged"
            />
            <Button
              class="shrink-0"
              :disabled="exporting"
              @click="exportToExcel"
            >
              <LoaderCircle v-if="exporting" class="animate-spin" />
              <FileSpreadsheet v-else />
              <span>{{ exporting ? 'Exportujem...' : 'Export do Excelu' }}</span>
            </Button>
          </div>
        </template>

        <div v-if="!loaded" class="flex justify-center py-16">
          <svg class="animate-spin size-8 text-brand-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        </div>

        <div class="max-w-full overflow-x-auto min-w-0" v-if="loaded">
            <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <TableHeader
                class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
              >
                <TableRow>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">ID Subjektu</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Subjekt</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">IČO</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Adresa</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Balík</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Zaplatené do</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Aktívne na adrese</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Označená schránka</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Poznámka</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Akcie</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                <TableEmptyState
                  v-if="loaded && !rows?.length"
                  title="Žiadne záznamy"
                  hint="Skúste upraviť hľadanie alebo filtre."
                  colspan="10"
                />
                <TableRow
                  v-for="row in rows"
                  :key="row.company_id"
                  class="hover:bg-brand-50 transition-colors cursor-pointer"
                  @click="goToDetail(row.company_id)"
                >
                <TableCell class="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{{ row.company_id }}</TableCell>
                <TableCell class="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white/90">{{ row.name }}</TableCell>
                <TableCell class="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{{ row.ico || '—' }}</TableCell>
                <TableCell class="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{{ row.owner_name || '—' }}</TableCell>
                <TableCell class="px-4 py-3 text-sm">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="{
                      'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300': row.sidlo_typ_balika === 'Mini',
                      'bg-brand-100 text-brand-700': row.sidlo_typ_balika === 'Ideal',
                      'bg-blue-100 text-blue-700': row.sidlo_typ_balika === 'Premium',
                      'bg-purple-100 text-purple-700': row.sidlo_typ_balika === 'Extra',
                      'bg-amber-100 text-amber-700': row.sidlo_typ_balika === 'Individual',
                    }">
                    {{ row.sidlo_typ_balika || '—' }}
                  </span>
                </TableCell>
                <TableCell class="px-4 py-3 text-sm font-medium" :class="{
                  'bg-red-100 text-red-700': getDateStatus(row.sidlo_zaplatene_do) === 'expired',
                  'bg-orange-100 text-orange-700': getDateStatus(row.sidlo_zaplatene_do) === 'warning',
                  'text-gray-600 dark:text-gray-300': getDateStatus(row.sidlo_zaplatene_do) === 'ok' || getDateStatus(row.sidlo_zaplatene_do) === 'none',
                }">{{ formatVhqDate(row.sidlo_zaplatene_do) }}</TableCell>
                <TableCell class="px-4 py-3 text-sm text-center">
                  <span v-if="row.active_on_address == true && row.active_on_address != null" class="text-green-500" title="Áno">✓</span>
                  <span v-else-if="row.active_on_address == false && row.active_on_address != null" class="text-red-400" title="Nie">✗</span>
                  <span v-else class="text-gray-300" title="Nenastavené">—</span>
                </TableCell>
                <TableCell class="px-4 py-3 text-sm text-center">
                  <span v-if="row.marked_mailbox == true && row.marked_mailbox != null" class="text-green-500" title="Áno">✓</span>
                  <span v-else-if="row.marked_mailbox == false && row.marked_mailbox != null" class="text-red-400" title="Nie">✗</span>
                  <span v-else class="text-gray-300" title="Nenastavené">—</span>
                </TableCell>
                <TableCell class="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate dark:text-gray-300" :title="row.custom_note || ''">
                  {{ row.custom_note || '—' }}
                </TableCell>
                <TableCell class="px-4 py-3 text-sm" @click.stop>
                  <Button size="xs" @click="goToDetail(row.company_id)">
                    <Eye class="size-4" />
                    <span>Detail</span>
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <template #footer>
          <TablePagination
            v-if="lastPage > 1"
            :current-page="currentPage"
            :total-pages="lastPage"
            @page-change="changePage"
          />
        </template>
      </TableCard>
    </div>
  </main>
</template>
